/**
 * Line-art coloring page for "The World Needs Your Light".
 * Renders at 8.5×11 aspect (816×1056 print px) with heavy black outlines
 * on pure white so kids can color in.
 *
 * Scene: the little bear cub from the cover, sitting on a hill under a
 * crescent moon, surrounded by pines, flowers, and twinkly stars — all
 * outlined only.
 */
export default function ColoringArtBear() {
  const stroke = "#111111";
  return (
    <svg
      viewBox="0 0 816 1056"
      xmlns="http://www.w3.org/2000/svg"
      style={{ width: "100%", height: "auto", background: "#FFFFFF" }}
      role="img"
      aria-label="Coloring page: a little bear looking at the moon"
    >
      {/* Title band */}
      <g fill="none" stroke={stroke} strokeWidth="2">
        <path d="M 40 60 Q 408 40 776 60" />
        <text x="408" y="110" textAnchor="middle" fontFamily="'Fredoka', sans-serif" fontSize="46" fill={stroke} stroke="none" fontWeight="700">
          The World Needs Your Light
        </text>
        <text x="408" y="150" textAnchor="middle" fontFamily="'Caveat', cursive" fontSize="30" fill={stroke} stroke="none">
          — a coloring page by M.K. Bunch
        </text>
      </g>

      {/* Ground hill */}
      <g fill="none" stroke={stroke} strokeWidth="4" strokeLinecap="round">
        <path d="M 0 820 Q 260 720 500 820 T 816 820 L 816 1056 L 0 1056 Z" />
        <path d="M 90 900 Q 200 860 320 900" />
        <path d="M 480 920 Q 600 880 730 920" />

        {/* Stars & moon */}
        <path d="M 120 220 Q 140 200 160 220 Q 175 250 160 280 Q 140 300 120 280 Q 110 250 120 220 Z" />
        <circle cx="620" cy="220" r="4" />
        <circle cx="700" cy="280" r="3" />
        <circle cx="560" cy="150" r="3" />
        <circle cx="200" cy="140" r="3" />
        <circle cx="80" cy="320" r="3" />
        <circle cx="740" cy="380" r="4" />
        <path d="M 380 190 l 6 -14 l 6 14 l 14 6 l -14 6 l -6 14 l -6 -14 l -14 -6 z" strokeLinejoin="round" />
        <path d="M 660 460 l 5 -12 l 5 12 l 12 5 l -12 5 l -5 12 l -5 -12 l -12 -5 z" strokeLinejoin="round" />

        {/* Pine trees */}
        <path d="M 40 820 L 80 720 L 60 730 L 100 640 L 80 650 L 120 560 L 160 650 L 140 640 L 180 730 L 160 720 L 200 820 Z" strokeLinejoin="round" />
        <path d="M 680 820 L 720 720 L 700 730 L 740 640 L 720 650 L 760 560 L 800 650 L 780 640 L 820 730 L 800 720 L 840 820 Z" strokeLinejoin="round" />

        {/* Small foreground pines */}
        <path d="M 240 900 L 270 820 L 260 828 L 290 760 L 280 767 L 305 700 L 340 767 L 330 760 L 360 828 L 350 820 L 380 900 Z" strokeLinejoin="round" />
        <path d="M 500 890 L 525 820 L 517 827 L 540 762 L 532 767 L 555 710 L 585 767 L 577 762 L 600 827 L 592 820 L 615 890 Z" strokeLinejoin="round" />

        {/* Bear cub */}
        {/* Ears */}
        <circle cx="335" cy="500" r="35" />
        <circle cx="335" cy="500" r="16" />
        <circle cx="480" cy="500" r="35" />
        <circle cx="480" cy="500" r="16" />
        {/* Head */}
        <ellipse cx="408" cy="560" rx="105" ry="95" />
        {/* Muzzle */}
        <ellipse cx="408" cy="600" rx="52" ry="38" />
        {/* Nose */}
        <ellipse cx="408" cy="580" rx="14" ry="10" fill={stroke} stroke="none" />
        {/* Mouth */}
        <path d="M 408 595 L 408 610" />
        <path d="M 408 610 Q 396 622 384 618" />
        <path d="M 408 610 Q 420 622 432 618" />
        {/* Eyes (open + sparkles) */}
        <circle cx="370" cy="545" r="8" fill={stroke} stroke="none" />
        <circle cx="446" cy="545" r="8" fill={stroke} stroke="none" />
        <circle cx="373" cy="542" r="2" fill="#FFFFFF" stroke="none" />
        <circle cx="449" cy="542" r="2" fill="#FFFFFF" stroke="none" />
        {/* Body */}
        <ellipse cx="408" cy="770" rx="150" ry="100" />
        {/* Belly patch */}
        <ellipse cx="408" cy="780" rx="90" ry="65" />
        {/* Left arm */}
        <ellipse cx="270" cy="740" rx="40" ry="55" transform="rotate(-18 270 740)" />
        {/* Right arm */}
        <ellipse cx="546" cy="740" rx="40" ry="55" transform="rotate(18 546 740)" />
        {/* Feet */}
        <ellipse cx="330" cy="875" rx="45" ry="22" />
        <ellipse cx="486" cy="875" rx="45" ry="22" />
        {/* Toe beans */}
        <circle cx="316" cy="875" r="4" />
        <circle cx="330" cy="880" r="4" />
        <circle cx="344" cy="875" r="4" />
        <circle cx="472" cy="875" r="4" />
        <circle cx="486" cy="880" r="4" />
        <circle cx="500" cy="875" r="4" />

        {/* Flowers on ground */}
        <g>
          {[130, 260, 560, 690].map((x, i) => (
            <g key={i} transform={`translate(${x} 940)`}>
              <circle r="9" />
              <circle r="3" />
              <path d="M -12 6 Q -18 20 -8 22" />
              <path d="M 12 6 Q 18 20 8 22" />
              <path d="M 0 12 L 0 30" />
            </g>
          ))}
        </g>
      </g>

      {/* Encouraging kicker */}
      <g>
        <text x="408" y="1010" textAnchor="middle" fontFamily="'Caveat', cursive" fontSize="32" fill={stroke}>
          Colour it any way that makes your heart glow ✿
        </text>
      </g>
    </svg>
  );
}
